/**
 * Adapted from React Bits' ElectricBorder component.
 * https://reactbits.dev/animations/electric-border
 * See LICENSE.react-bits.
 */
import React, {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import './ElectricBorder.css';
import { useTheme } from '@mui/material/styles';

interface ElectricBorderProps {
  children?: ReactNode;
  color?: string;
  accentColor?: string;
  speed?: number;
  chaos?: number;
  borderRadius?: number;
  className?: string;
  style?: CSSProperties;
}

const ElectricBorder: React.FC<ElectricBorderProps> = ({
  children,
  color: suppliedColor,
  accentColor: suppliedAccentColor,
  speed = 0.7,
  chaos = 0.08,
  borderRadius = 24,
  className = '',
  style,
}) => {
  const theme = useTheme();
  const color = suppliedColor ?? theme.palette.warning.light;
  const accentColor = suppliedAccentColor ?? theme.palette.warning.dark;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const timeRef = useRef(0);
  const lastFrameTimeRef = useRef(0);

  const random = useCallback(
    (x: number) => (Math.sin(x * 12.9898) * 43758.5453) % 1,
    []
  );

  const noise2D = useCallback(
    (x: number, y: number) => {
      const i = Math.floor(x);
      const j = Math.floor(y);
      const fx = x - i;
      const fy = y - j;
      const ux = fx * fx * (3 - 2 * fx);
      const uy = fy * fy * (3 - 2 * fy);
      const a = random(i + j * 57);
      const b = random(i + 1 + j * 57);
      const c = random(i + (j + 1) * 57);
      const d = random(i + 1 + (j + 1) * 57);

      return (
        a * (1 - ux) * (1 - uy) +
        b * ux * (1 - uy) +
        c * (1 - ux) * uy +
        d * ux * uy
      );
    },
    [random]
  );

  const octavedNoise = useCallback(
    (x: number, time: number, seed: number) => {
      let value = 0;
      let amplitude = chaos;
      let frequency = 10;

      for (let octave = 0; octave < 8; octave += 1) {
        value +=
          amplitude *
          noise2D(frequency * x + seed * 100, time * frequency * 0.3);
        frequency *= 1.6;
        amplitude *= 0.7;
      }

      return value;
    },
    [chaos, noise2D]
  );

  const cornerPoint = useCallback(
    (
      centerX: number,
      centerY: number,
      radius: number,
      startAngle: number,
      progress: number
    ) => {
      const angle = startAngle + progress * (Math.PI / 2);
      return {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      };
    },
    []
  );

  const roundedRectPoint = useCallback(
    (
      t: number,
      left: number,
      top: number,
      width: number,
      height: number,
      radius: number
    ) => {
      const horizontal = width - 2 * radius;
      const vertical = height - 2 * radius;
      const arc = (Math.PI * radius) / 2;
      const distance = t * (2 * horizontal + 2 * vertical + 4 * arc);
      let cursor = 0;

      if (distance <= cursor + horizontal) {
        const progress = (distance - cursor) / horizontal;
        return { x: left + radius + progress * horizontal, y: top };
      }
      cursor += horizontal;
      if (distance <= cursor + arc) {
        return cornerPoint(
          left + width - radius,
          top + radius,
          radius,
          -Math.PI / 2,
          (distance - cursor) / arc
        );
      }
      cursor += arc;
      if (distance <= cursor + vertical) {
        const progress = (distance - cursor) / vertical;
        return { x: left + width, y: top + radius + progress * vertical };
      }
      cursor += vertical;
      if (distance <= cursor + arc) {
        return cornerPoint(
          left + width - radius,
          top + height - radius,
          radius,
          0,
          (distance - cursor) / arc
        );
      }
      cursor += arc;
      if (distance <= cursor + horizontal) {
        const progress = (distance - cursor) / horizontal;
        return {
          x: left + width - radius - progress * horizontal,
          y: top + height,
        };
      }
      cursor += horizontal;
      if (distance <= cursor + arc) {
        return cornerPoint(
          left + radius,
          top + height - radius,
          radius,
          Math.PI / 2,
          (distance - cursor) / arc
        );
      }
      cursor += arc;
      if (distance <= cursor + vertical) {
        const progress = (distance - cursor) / vertical;
        return { x: left, y: top + height - radius - progress * vertical };
      }

      return cornerPoint(
        left + radius,
        top + radius,
        radius,
        Math.PI,
        (distance - cursor - vertical) / arc
      );
    },
    [cornerPoint]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const context = canvas?.getContext('2d');
    const reducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!canvas || !container || !context || reducedMotion) return;

    const offset = 28;
    const displacement = 34;
    let width = 0;
    let height = 0;

    const updateSize = () => {
      const rect = container.getBoundingClientRect();
      width = rect.width + offset * 2;
      height = rect.height + offset * 2;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    };

    const draw = (currentTime: number) => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const deltaTime = lastFrameTimeRef.current
        ? (currentTime - lastFrameTimeRef.current) / 1000
        : 0;
      timeRef.current += deltaTime * speed;
      lastFrameTimeRef.current = currentTime;

      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.scale(dpr, dpr);
      context.strokeStyle = color;
      context.lineWidth = 1.25;
      context.lineCap = 'round';
      context.lineJoin = 'round';

      const borderWidth = width - offset * 2;
      const borderHeight = height - offset * 2;
      const radius = Math.min(
        borderRadius,
        Math.min(borderWidth, borderHeight) / 2
      );
      const perimeter = 2 * (borderWidth + borderHeight) + 2 * Math.PI * radius;
      const samples = Math.max(80, Math.floor(perimeter / 2));

      context.beginPath();
      for (let index = 0; index <= samples; index += 1) {
        const progress = index / samples;
        const point = roundedRectPoint(
          progress,
          offset,
          offset,
          borderWidth,
          borderHeight,
          radius
        );
        const x =
          point.x +
          octavedNoise(progress * 8, timeRef.current, 0) * displacement;
        const y =
          point.y +
          octavedNoise(progress * 8, timeRef.current, 1) * displacement;

        if (index === 0) context.moveTo(x, y);
        else context.lineTo(x, y);
      }
      context.closePath();
      context.stroke();
      animationRef.current = requestAnimationFrame(draw);
    };

    updateSize();
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(updateSize);
    resizeObserver?.observe(container);
    animationRef.current = requestAnimationFrame(draw);

    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
      resizeObserver?.disconnect();
    };
  }, [borderRadius, color, octavedNoise, roundedRectPoint, speed]);

  const variables = {
    '--electric-border-color': color,
    '--electric-border-accent': accentColor,
    borderRadius,
  } as CSSProperties;

  return (
    <div
      ref={containerRef}
      className={`sv-electric-border ${className}`}
      style={{ ...variables, ...style }}
    >
      <div className="sv-electric-border__canvas-wrap" aria-hidden="true">
        <canvas ref={canvasRef} className="sv-electric-border__canvas" />
      </div>
      <div className="sv-electric-border__layers" aria-hidden="true">
        <div className="sv-electric-border__glow sv-electric-border__glow--tight" />
        <div className="sv-electric-border__glow sv-electric-border__glow--soft" />
        <div className="sv-electric-border__ambient" />
      </div>
      <div className="sv-electric-border__content">{children}</div>
    </div>
  );
};

export default ElectricBorder;
